import React, { useEffect, useMemo, useState } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, CartesianGrid } from "recharts";
import api from "../lib/api";
import { formatTRY } from "../lib/mw";
import { downloadFile } from "../lib/download";
import { Download } from "lucide-react";

const PAL = ["#3b82f6", "#10b981", "#f59e0b", "#ec4899", "#8b5cf6", "#06b6d4", "#ef4444", "#84cc16", "#f97316", "#14b8a6"];
const METRICS = [["ciro", "TOPLAM CİRO (TÜM KARGOLAR)"], ["giden_ciro", "ŞUBELER ARASI GİDEN CİRO"], ["tahsilat", "TAHSİLAT (KASA)"]];
const short = (v) => (Math.abs(v) >= 1e6 ? `${(v / 1e6).toLocaleString("tr-TR", { maximumFractionDigits: 1 })} MN` : Math.abs(v) >= 1e3 ? `${(v / 1e3).toLocaleString("tr-TR", { maximumFractionDigits: 0 })} B` : String(v));

export const BranchMonthlyChart = ({ d1, d2 }) => {
  const [rows, setRows] = useState([]);
  const [metric, setMetric] = useState("ciro");
  useEffect(() => { api.get("/org/monthly", { params: { d1, d2 } }).then((r) => setRows(r.data)).catch(() => setRows([])); }, [d1, d2]);
  const subeler = useMemo(() => [...new Set(rows.map((r) => r.sube))], [rows]);
  const data = useMemo(() => {
    const m = {};
    rows.forEach((r) => { m[r.ay] = m[r.ay] || { ay: r.ay }; m[r.ay][r.sube] = r[metric]; });
    return Object.values(m).sort((a, b) => a.ay.localeCompare(b.ay));
  }, [rows, metric]);
  return (
    <div data-testid="branch-monthly-chart" className="rounded-xl border border-slate-800 bg-slate-900 p-3">
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <span className="font-bold text-slate-100">ŞUBE AYLIK CİRO / TAHSİLAT</span>
        {METRICS.map(([k, l]) => (
          <button key={k} data-testid={`chart-metric-${k}`} onClick={() => setMetric(k)} className={`h-8 rounded px-3 text-xs font-bold ${metric === k ? "bg-blue-600 text-white" : "border border-slate-700 text-slate-300 hover:bg-slate-800"}`}>{l}</button>
        ))}
        <button data-testid="chart-excel-download" onClick={() => downloadFile(`/org/monthly?fmt=xlsx&d1=${d1}&d2=${d2}`, `sube_aylik_${d1}_${d2}.xlsx`)} className="ml-auto flex h-8 items-center gap-1 rounded bg-emerald-700 px-3 text-xs font-bold text-white hover:bg-emerald-800"><Download className="h-3.5 w-3.5" /> EXCEL İNDİR</button>
      </div>
      {data.length === 0 ? <div className="py-10 text-center text-xs text-slate-500">BU ARALIKTA VERİ YOK</div> : (
        <div className="h-[320px] w-full">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data} margin={{ top: 8, right: 8, left: 8, bottom: 0 }}>
              <CartesianGrid stroke="#1e293b" vertical={false} />
              <XAxis dataKey="ay" tick={{ fill: "#94a3b8", fontSize: 11 }} />
              <YAxis tickFormatter={short} tick={{ fill: "#94a3b8", fontSize: 11 }} width={60} />
              <Tooltip cursor={{ fill: "rgba(148,163,184,0.08)" }} formatter={(v) => formatTRY(v)} contentStyle={{ background: "#0f172a", border: "1px solid #334155", fontSize: 12 }} labelStyle={{ color: "#e2e8f0" }} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              {subeler.map((s, i) => <Bar key={s} dataKey={s} fill={PAL[i % PAL.length]} radius={[3, 3, 0, 0]} />)}
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
      {rows.length > 0 && (
        <div className="mt-2 max-h-[220px] overflow-auto">
          <table data-testid="monthly-table" className="w-full text-xs">
            <thead className="sticky top-0 bg-slate-900 text-left text-slate-400"><tr><th className="py-1">AY</th><th>ŞUBE</th><th className="text-right">TOPLAM CİRO</th><th className="text-right">ŞUBELER ARASI GİDEN</th><th className="text-right">TAHSİLAT</th></tr></thead>
            <tbody>{rows.map((r) => <tr key={r.ay + r.sube_id} className="border-t border-slate-800 text-slate-200"><td className="py-1 font-mono">{r.ay}</td><td>{r.sube}</td><td className="text-right font-mono">{formatTRY(r.ciro)}</td><td className="text-right font-mono">{formatTRY(r.giden_ciro)}</td><td className="text-right font-mono">{formatTRY(r.tahsilat)}</td></tr>)}</tbody>
          </table>
        </div>
      )}
    </div>
  );
};
